import { type Component, createEffect, createMemo, createSignal, createUniqueId, For, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import type { OptionType, valueType } from '~/types/Select'
import type { SelectModalPropsType } from '~/types/SelectModal'
import { controlSize } from '~/utils/controlSize'
import Checkbox from './Checkbox'
import Chip from './Chip'
import FieldMessage from './FieldMessage'
import Icon from './Icon'
import ListItem from './ListItem'
import Modal from './Modal'
import Radio from './Radio'

const SelectModal: Component<SelectModalPropsType> = props => {
  const [isOpen, setIsOpen] = createSignal(false)
  const [rawSearch, setRawSearch] = createSignal('')
  const [search, setSearch] = createSignal('')
  const [isFocused, setIsFocused] = createSignal(false)
  const [selectedState, setSelectedState] = createSignal<valueType>(props.value || (props.multiple ? [] : ''))
  const id = createUniqueId()
  const labelId = createUniqueId()

  const ITEM_HEIGHT = 48
  const CONTAINER_HEIGHT = 400

  createEffect(() => {
    if (props.value !== undefined) {
      setSelectedState(props.value)
    }
  })

  let searchTimer: number | undefined
  createEffect(() => {
    const q = rawSearch()
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = window.setTimeout(() => setSearch(q), 250)
  })

  const normalize = (s?: string) =>
    (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()

  const levenshtein = (a: string, b: string) => {
    if (a === b) return 0
    const al = a.length
    const bl = b.length
    if (al === 0) return bl
    if (bl === 0) return al

    let prev = new Array(bl + 1)
    let cur = new Array(bl + 1)
    for (let j = 0; j <= bl; j++) prev[j] = j

    for (let i = 1; i <= al; i++) {
      cur[0] = i
      const ai = a.charAt(i - 1)
      for (let j = 1; j <= bl; j++) {
        const cost = ai === b.charAt(j - 1) ? 0 : 1
        const deletion = prev[j] + 1
        const insertion = cur[j - 1] + 1
        const substitution = prev[j - 1] + cost
        cur[j] = Math.min(deletion, insertion, substitution)
      }
      const tmp = prev
      prev = cur
      cur = tmp
    }
    return prev[bl]
  }

  const filteredOptions = createMemo(() => {
    const q = normalize(search())
    if (!q) return props.options

    const tokens = q.split(/[^\p{L}\p{N}]+/u).filter(Boolean)

    const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const scored = props.options
      .map(opt => {
        const title = normalize(opt.title)
        const subtitle = normalize(opt.subtitle || '')
        let score = 0

        for (const t of tokens) {
          const isNumeric = /^\d+$/.test(t)
          const wordRe = new RegExp(`\\b${escapeRegex(t)}\\b`, 'u')
          let tokenMatched = false

          if (wordRe.test(title)) {
            score += isNumeric ? 300 : 200
            tokenMatched = true
          } else if (title.startsWith(t)) {
            score += 80
            tokenMatched = true
          } else if (title.includes(t)) {
            score += isNumeric ? 20 : 30
            tokenMatched = true
          } else {
            const threshold = Math.max(1, Math.floor(t.length * 0.35))
            const d = levenshtein(title, t)
            if (d <= threshold) {
              score += Math.max(10, 20 - d * 5)
              tokenMatched = true
            }
          }

          if (wordRe.test(subtitle)) {
            score += 30
            tokenMatched = true
          } else if (subtitle.includes(t)) {
            score += 8
            tokenMatched = true
          } else {
            const thresholdSub = Math.max(1, Math.floor(t.length * 0.3))
            const ds = levenshtein(subtitle, t)
            if (ds <= thresholdSub) {
              score += Math.max(3, 8 - ds * 2)
              tokenMatched = true
            }
          }

          if (!tokenMatched) {
            return { opt, score: 0 }
          }
        }

        return { opt, score }
      })
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(x => x.opt as OptionType)

    return scored
  })

  createEffect(() => {
    void search()
    if (listRef) {
      listRef.scrollTop = 0
      setViewerTop(0)
    }
  })

  let listRef: HTMLDivElement | undefined
  const [viewerTop, setViewerTop] = createSignal(0)
  const VISIBLE_COUNT = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT) + 5

  const startIndex = createMemo(() => Math.max(0, Math.floor(viewerTop() / ITEM_HEIGHT)))
  const visibleItems = createMemo(() => {
    const items = filteredOptions()
    return items.slice(startIndex(), startIndex() + VISIBLE_COUNT)
  })
  const containerHeightTotal = createMemo(() => Math.max(filteredOptions().length * ITEM_HEIGHT, CONTAINER_HEIGHT))

  const onVirtualScroll = () => {
    if (!listRef) return
    setViewerTop(listRef.scrollTop || 0)
  }

  const selectedValues = createMemo(() => {
    const selected = selectedState()
    return Array.isArray(selected) ? selected : [selected].filter(Boolean)
  })

  const isOptionSelected = (optionValue: string | number) => selectedValues().includes(optionValue)

  const handleSelect = (option: OptionType) => {
    let newValue: string | number | (string | number)[]
    if (props.multiple) {
      const current = Array.isArray(selectedState()) ? [...(selectedState() as (string | number)[])] : []
      const index = current.indexOf(option.value)
      if (index > -1) {
        current.splice(index, 1)
      } else {
        current.push(option.value)
      }
      newValue = current
    } else {
      newValue = option.value
      setIsOpen(false)
    }
    setSelectedState(newValue)
    props.onChange?.(newValue)
  }

  const removeSelected = (optionValue: string | number, _e: MouseEvent) => {
    if (props.multiple) {
      const current = Array.isArray(selectedState()) ? (selectedState() as (string | number)[]) : []
      const newSelected = current.filter(v => v !== optionValue)

      setSelectedState(newSelected)
      props.onChange?.(newSelected)
    } else {
      setSelectedState('')
      props.onChange?.('')
    }
  }

  const getSelectedLabels = createMemo(() => {
    const currentSelected = selectedState()
    if (props.multiple) {
      const values = Array.isArray(currentSelected) ? currentSelected : []
      return values
        .map(v => {
          const option = props.options.find(opt => opt.value === v)
          return option ? { value: option.value, label: option.title } : null
        })
        .filter(Boolean)
    }
    const selectedOption = props.options.find(opt => opt.value === currentSelected)
    return selectedOption ? [{ value: selectedOption.value, label: selectedOption.title }] : []
  })

  const hasValue = createMemo(() => {
    const currentSelected = selectedState()
    if (props.multiple) {
      return Array.isArray(currentSelected) && currentSelected.length > 0
    }

    return currentSelected !== '' && currentSelected !== undefined && currentSelected !== null
  })

  const isLabelFloating = createMemo(() => isFocused() || isOpen() || hasValue() || !!props.placeholder)

  const hasResults = createMemo(() => filteredOptions().length > 0)

  // Admitía `size` en las props pero lo ignoraba: iba fijo a 48 px mientras el
  // resto de campos seguía su talla. Ahora usa la escala común.
  const sizeConfig = createMemo(() => controlSize(props.size))

  const triggerClasses = createMemo(() =>
    twMerge(
      'bg-surface-muted text-foreground rounded-lg flex items-center w-full transition-colors relative border border-transparent focus:outline-none',
      sizeConfig().height,
      sizeConfig().text,
      sizeConfig().padX,
      props.label ? sizeConfig().labelPad : 'py-0',
      props.disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
      props.error ? 'border-danger!' : ''
    )
  )

  const labelClasses = createMemo(() =>
    twMerge(
      'absolute block select-none whitespace-nowrap duration-100 transition-all origin-left z-10 max-w-[calc(100%-3rem)] overflow-hidden text-ellipsis',
      sizeConfig().padStart,
      props.error ? 'text-danger' : 'text-foreground',
      isLabelFloating() ? `${sizeConfig().labelFloating} top-1 translate-y-0` : `${sizeConfig().text} top-1/2 -translate-y-1/2`,
      props.disabled ? 'opacity-70 cursor-not-allowed' : isLabelFloating() ? 'cursor-default' : 'cursor-pointer'
    )
  )

  const searchInputClasses = createMemo(() => twMerge('bg-surface-muted text-foreground text-sm rounded-lg block w-full pl-10 p-2.5 focus:outline-none'))

  const optionItemClasses = createMemo(() => twMerge('group cursor-pointer select-none transition-all duration-200 hover:bg-surface-hover'))

  return (
    <div class="relative w-full">
      {/* Select Trigger */}
      <div
        onClick={() => !props.disabled && setIsOpen(true)}
        onFocus={() => !props.disabled && setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        tabIndex={props.disabled ? -1 : 0}
        class={triggerClasses()}
        id={id}
        aria-labelledby={labelId}
      >
        <div class="flex flex-wrap gap-2 items-center min-h-4.5">
          <Show when={props.startIcon}>
            <Icon icon={props.startIcon!} size={20} class="text-slate-400 mr-2 shrink-0" />
          </Show>
          <Show when={hasValue()} fallback={<span class="text-slate-400 select-none truncate">{props.placeholder}</span>}>
            <For each={getSelectedLabels()}>
              {item =>
                item && (
                  <Chip color="primary" variant="soft" size="sm" removable onRemove={e => removeSelected(item.value, e)}>
                    {item.label}
                  </Chip>
                )
              }
            </For>
          </Show>
        </div>
        <div class="bg-surface-muted rounded-lg absolute top-1/2 -translate-y-1/2 right-0 flex items-center pr-2 pointer-events-none">
          <Icon icon="material-symbols-light:expand-more" size={20} class="text-foreground-muted" />
        </div>
      </div>

      {/* Label with floating style */}
      <Show when={props.label}>
        <span onClick={() => !props.disabled && setIsOpen(true)} id={labelId} class={labelClasses()} style={{ 'pointer-events': isLabelFloating() ? 'none' : 'auto' }}>
          {props.label}
        </span>
      </Show>
      <FieldMessage id={`${id}-message`} size={props.size} error={props.error} helperText={props.helperText} />

      <Modal closable isOpen={isOpen()} onClose={() => setIsOpen(false)}>
        <Modal.Header>
          <div class="flex items-center justify-between">{props.label || 'Selecciona opciones'}</div>
        </Modal.Header>

        {/* Search - Conditionally Rendered */}
        <Show when={props.search === true}>
          <div class="px-6 pb-0 pt-2 shrink-0">
            <div class="relative">
              <input type="text" class={searchInputClasses()} placeholder="Buscar..." value={rawSearch()} onInput={e => setRawSearch(e.currentTarget.value)} />
            </div>
          </div>
        </Show>

        {/* Options List con Virtualización */}
        <div class="overflow-auto relative flex-1" style={{ height: `${CONTAINER_HEIGHT}px` }} onScroll={onVirtualScroll} ref={el => (listRef = el)}>
          {/* Contenedor virtual con altura total */}
          <div style={{ height: `${containerHeightTotal()}px`, position: 'relative' }}>
            {/* Elementos visibles renderizados en un wrapper desplazado */}
            <div style={{ transform: `translateY(${startIndex() * ITEM_HEIGHT}px)` }}>
              <For each={visibleItems()}>
                {option => {
                  const isSelected = isOptionSelected(option.value)
                  return (
                    <div class="w-full" style={{ height: `${ITEM_HEIGHT}px` }}>
                      <ListItem selected={() => isOptionSelected(option.value)} class={optionItemClasses()} disabled={props.disabled} onClick={() => handleSelect(option)}>
                        <Show when={props.multiple === true}>
                          <Checkbox checked={() => isOptionSelected(option.value)} disabled={option.disabled} onChange={() => handleSelect(option)} />
                        </Show>

                        <Show when={props.multiple !== true}>
                          <Radio checked={isSelected} disabled={option.disabled} onChange={() => handleSelect(option)} />
                        </Show>

                        <div class="flex items-start flex-col">
                          <span class="ml-2">{option.title}</span>
                          <span class="ml-2 text-xs text-foreground-subtle">{option.subtitle}</span>
                        </div>
                      </ListItem>
                    </div>
                  )
                }}
              </For>
            </div>
          </div>

          {/* Mensaje cuando no hay resultados: siempre visible dentro del viewport */}
          <Show when={!hasResults()}>
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div class="text-center py-8 text-foreground-subtle">No se encontraron resultados</div>
            </div>
          </Show>
        </div>
      </Modal>
    </div>
  )
}

export default SelectModal
